import { useMemo, useRef, useState } from "react";

import {
  SegmentationSurveyCompletedV3,
  SegmentationSurveyStep3dProficiencyCompleted,
  SegmentationSurveyStepDesignIntentCompleted,
  SegmentationSurveyStepPrimaryCadCompleted,
  SelectButton,
  SpinnerCenter,
} from "@shapr3d/frontend-lib";
import { shuffle } from "es-toolkit";

import useNavigation from "../../hooks/navigation";
import { useSetAppProperties } from "../../hooks/useGetAppProperties";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import Heading from "../Heading/Heading";
import MultistepForm from "../MultistepForm/MultistepForm";
import ProgressiveProfilingError from "../ProgressiveProfilingError/ProgressiveProfilingError";

import style from "./SegmentationSurvey.module.css";

type UsageIntent = "work_only" | "hobby_only" | "work_and_hobby" | "education" | "first_tool";

type DesignIntent = SegmentationSurveyStepDesignIntentCompleted["eventProperties"]["design_intent"];
type PrimaryCadTool = SegmentationSurveyStepPrimaryCadCompleted["eventProperties"]["cad_tool"];
type Proficiency = SegmentationSurveyStep3dProficiencyCompleted["eventProperties"]["proficiency"];

const DESIGN_INTENT_MAP: Record<UsageIntent, DesignIntent> = {
  work_only: "work",
  hobby_only: "hobby",
  work_and_hobby: "work_and_hobby",
  education: "education",
  first_tool: "first_3d_design_tool",
};

// Steps per branch (after Q1). Order here determines step index order.
// work / work_and_hobby:  cadWork appears before cadHobby
// hobby / work_and_hobby: cadHobby appears after cadWork slot
// education:              cadGeneral
// first_tool:             no further steps
const FOLLOW_UP_STEPS = {
  work_only: ["cadWork", "proficiency"],
  hobby_only: ["cadHobby", "proficiency"],
  work_and_hobby: ["cadWork", "cadHobby", "proficiency"],
  education: ["cadGeneral", "proficiency"],
  first_tool: [] as string[],
} satisfies Record<UsageIntent, string[]>;

const SegmentationSurvey = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [usageIntent, setUsageIntent] = useState<UsageIntent | undefined>();
  const [toolWork, setToolWork] = useState<PrimaryCadTool | undefined>();
  const [toolHobby, setToolHobby] = useState<PrimaryCadTool | undefined>();
  const [toolGeneral, setToolGeneral] = useState<PrimaryCadTool | undefined>();
  const [proficiency, setProficiency] = useState<Proficiency | undefined>();
  const [submitState, setSubmitState] = useState<"initial" | "loading" | "error">("initial");

  // Written on every render so finishSurvey always reads fresh values
  // regardless of which render's closure it was captured in.
  const latestAnswers = useRef({ usageIntent, toolWork, toolHobby, toolGeneral, proficiency });
  latestAnswers.current = { usageIntent, toolWork, toolHobby, toolGeneral, proficiency };

  const navigation = useNavigation();
  const tracker = useTracker();
  const { mutateAsync: mutateAppProperties } = useSetAppProperties();
  const { t, m } = useTranslation();

  const cadTools = useMemo<Array<{ label: string; value: PrimaryCadTool }>>(
    () => [
      { label: t(m.SegmentationSurvey.QuestionThree.None), value: "none" },
      { label: t(m.SegmentationSurvey.QuestionThree.ThreedsMax), value: "3ds_max" },
      { label: t(m.SegmentationSurvey.QuestionThree.Alias), value: "alias" },
      { label: t(m.SegmentationSurvey.QuestionThree.Archicad), value: "archicad" },
      { label: t(m.SegmentationSurvey.QuestionThree.Autocad), value: "autocad" },
      { label: t(m.SegmentationSurvey.QuestionThree.Catia), value: "catia" },
      { label: t(m.SegmentationSurvey.QuestionThree.Cinema4d), value: "cinema_4d" },
      { label: t(m.SegmentationSurvey.QuestionThree.Fusion), value: "fusion_360" },
      { label: t(m.SegmentationSurvey.QuestionThree.Inventor), value: "inventor" },
      { label: t(m.SegmentationSurvey.QuestionThree.Onshape), value: "onshape" },
      { label: t(m.SegmentationSurvey.QuestionThree.Creo), value: "creo" },
      { label: t(m.SegmentationSurvey.QuestionThree.Revit), value: "revit" },
      { label: t(m.SegmentationSurvey.QuestionThree.Rhino), value: "rhino" },
      { label: t(m.SegmentationSurvey.QuestionThree.Sketchup), value: "sketchup" },
      { label: t(m.SegmentationSurvey.QuestionThree.SolidEdge), value: "solid_edge" },
      { label: t(m.SegmentationSurvey.QuestionThree.SolidWorks), value: "solidworks" },
      { label: t(m.SegmentationSurvey.QuestionThree.Siemens), value: "siemens_nx" },
      { label: t(m.SegmentationSurvey.QuestionThree.SpaceClaim), value: "spaceclaim" },
      { label: t(m.SegmentationSurvey.QuestionThree.TinkerCad), value: "tinkercad" },
      { label: t(m.SegmentationSurvey.QuestionThree.Plasticity), value: "plasticity" },
      { label: t(m.SegmentationSurvey.QuestionThree.Maya), value: "maya" },
      { label: t(m.SegmentationSurvey.QuestionThree.FreeCad), value: "freecad" },
      { label: t(m.SegmentationSurvey.QuestionThree.Shapr3D), value: "shapr3d" },
      { label: t(m.SegmentationSurvey.QuestionThree.Vectorworks), value: "vectorworks" },
      { label: t(m.SegmentationSurvey.QuestionThree.Blender), value: "blender" },
    ],
    [t, m]
  );

  const finishSurvey = () => {
    const { usageIntent, toolWork, toolHobby, toolGeneral, proficiency } = latestAnswers.current;

    const events: (
      | SegmentationSurveyCompletedV3
      | SegmentationSurveyStep3dProficiencyCompleted
      | SegmentationSurveyStepDesignIntentCompleted
      | SegmentationSurveyStepPrimaryCadCompleted
    )[] = [];

    if (usageIntent) {
      events.push(
        new SegmentationSurveyStepDesignIntentCompleted({
          designIntent: DESIGN_INTENT_MAP[usageIntent],
        })
      );
    }

    if (toolWork) {
      events.push(
        new SegmentationSurveyStepPrimaryCadCompleted({
          cadTool: toolWork,
          context: "work",
        })
      );
    }

    if (toolHobby) {
      events.push(
        new SegmentationSurveyStepPrimaryCadCompleted({
          cadTool: toolHobby,
          context: "hobby",
        })
      );
    }

    if (toolGeneral) {
      events.push(
        new SegmentationSurveyStepPrimaryCadCompleted({
          cadTool: toolGeneral,
          context: "education",
        })
      );
    }

    if (proficiency) {
      events.push(
        new SegmentationSurveyStep3dProficiencyCompleted({
          proficiency,
        })
      );
    }

    events.push(new SegmentationSurveyCompletedV3());
    tracker.trackSegmentationSurveyV4Events(events);

    setSubmitState("loading");
    const finishedAt = new Date().toISOString();
    mutateAppProperties(
      {
        is_resegmentation_needed: false,
        segmentation_work_completed: true,
        segmentation_work_web_completed: true,
        segmentation_is_student_or_teacher: false,
        segmentation_v2_finished_at: finishedAt,
        segmentation_v2_progressive_profiling_finished_at: finishedAt,
      },
      {
        onSuccess: () => {
          navigation.tryRedirectFromSearchParams();
        },
        onError: () => {
          setSubmitState("error");
        },
      }
    );
  };

  const shuffledTools = useMemo(() => shuffle(cadTools), [cadTools]);

  // Derive which value the current visible step holds (for nextDisabled)
  const currentStepValue = (() => {
    if (currentStep === 0) return usageIntent;
    if (!usageIntent) return undefined;
    const followUp = FOLLOW_UP_STEPS[usageIntent][currentStep - 1];
    switch (followUp) {
      case "cadWork":
        return toolWork;
      case "cadHobby":
        return toolHobby;
      case "cadGeneral":
        return toolGeneral;
      case "proficiency":
        return proficiency;
    }
  })();

  const totalSteps = usageIntent ? 1 + FOLLOW_UP_STEPS[usageIntent].length : 1;

  const goToNext = () => {
    if (currentStep + 1 >= totalSteps) {
      finishSurvey();
    } else {
      setTimeout(() => setCurrentStep((s) => s + 1), 100);
    }
  };

  const handleQ1Change = (value: string) => {
    const intent = value as UsageIntent;
    setUsageIntent(intent);
    setToolWork(undefined);
    setToolHobby(undefined);
    setToolGeneral(undefined);
    setProficiency(undefined);
    const followUp = FOLLOW_UP_STEPS[intent];
    setTimeout(() => {
      if (followUp.length === 0) {
        finishSurvey();
      } else {
        setCurrentStep(1);
      }
    }, 100);
  };

  if (submitState === "loading") return <SpinnerCenter />;
  if (submitState === "error")
    return <ProgressiveProfilingError retryCallback={() => setSubmitState("initial")} />;

  return (
    <div className={style.container}>
      <MultistepForm
        submitCallback={(e) => {
          e.preventDefault();
          finishSurvey();
        }}
        currentStep={currentStep}
        goToNextCallback={goToNext}
        goToPreviousCallback={() => setCurrentStep((s) => s - 1)}
        previousDisabled={false}
        nextDisabled={!currentStepValue}
      >
        <MultistepForm.Step>
          <Heading type="h1">{t(m.SegmentationSurvey.General.TailorExperience)}</Heading>
          <Heading type="h2" className={style.label}>
            {t(m.SegmentationSurvey.General.WhatDoYouUseFor)}
          </Heading>
          <SelectButton
            className={style.radioGroup}
            value={usageIntent ?? ""}
            onChange={handleQ1Change}
            type="radio"
            size="small"
          >
            <SelectButton.Item title={t(m.SegmentationSurvey.General.Work)} value="work_only" />
            <SelectButton.Item title={t(m.SegmentationSurvey.General.Hobby)} value="hobby_only" />
            <SelectButton.Item
              title={t(m.SegmentationSurvey.General.WorkAndHobby)}
              value="work_and_hobby"
            />
            <SelectButton.Item
              title={t(m.SegmentationSurvey.General.EducationIntent)}
              value="education"
            />
            <SelectButton.Item
              title={t(m.SegmentationSurvey.General.FirstDesignTool)}
              value="first_tool"
            />
          </SelectButton>
        </MultistepForm.Step>

        {(usageIntent === "work_only" || usageIntent === "work_and_hobby") && (
          <MultistepForm.Step>
            <Heading type="h2" className={style.label}>
              {t(m.SegmentationSurvey.QuestionCad.PrimaryToolForWork, {
                b: (chunks) => <b>{chunks}</b>,
              })}
            </Heading>
            <SelectButton
              className={style.radioGroup}
              value={toolWork ?? ""}
              onChange={(value) => {
                setToolWork(value as PrimaryCadTool);
                setTimeout(goToNext, 100);
              }}
              type="radio"
              size="small"
            >
              {shuffledTools.map((tool) => (
                <SelectButton.Item key={tool.value} title={tool.label} value={tool.value} />
              ))}
              <SelectButton.Item
                title={t(m.SegmentationSurvey.QuestionThree.Other)}
                value="other"
              />
            </SelectButton>
          </MultistepForm.Step>
        )}

        {(usageIntent === "hobby_only" || usageIntent === "work_and_hobby") && (
          <MultistepForm.Step>
            <Heading type="h2" className={style.label}>
              {t(m.SegmentationSurvey.QuestionCad.PrimaryToolForHobby, {
                b: (chunks) => <b>{chunks}</b>,
              })}
            </Heading>
            <SelectButton
              className={style.radioGroup}
              value={toolHobby ?? ""}
              onChange={(value) => {
                setToolHobby(value as PrimaryCadTool);
                setTimeout(goToNext, 100);
              }}
              type="radio"
              size="small"
            >
              {shuffledTools.map((tool) => (
                <SelectButton.Item key={tool.value} title={tool.label} value={tool.value} />
              ))}
              <SelectButton.Item
                title={t(m.SegmentationSurvey.QuestionThree.Other)}
                value="other"
              />
            </SelectButton>
          </MultistepForm.Step>
        )}

        {usageIntent === "education" && (
          <MultistepForm.Step>
            <Heading type="h2" className={style.label}>
              {t(m.SegmentationSurvey.QuestionCad.PrimaryToolGeneral)}
            </Heading>
            <SelectButton
              className={style.radioGroup}
              value={toolGeneral ?? ""}
              onChange={(value) => {
                setToolGeneral(value as PrimaryCadTool);
                setTimeout(goToNext, 100);
              }}
              type="radio"
              size="small"
            >
              {shuffledTools.map((tool) => (
                <SelectButton.Item key={tool.value} title={tool.label} value={tool.value} />
              ))}
              <SelectButton.Item
                title={t(m.SegmentationSurvey.QuestionThree.Other)}
                value="other"
              />
            </SelectButton>
          </MultistepForm.Step>
        )}

        {Boolean(usageIntent) && usageIntent !== "first_tool" && (
          <MultistepForm.Step>
            <Heading type="h2" className={style.label}>
              {t(m.SegmentationSurvey.QuestionProficiency.Title)}
            </Heading>
            <SelectButton
              className={style.radioGroup}
              value={proficiency ?? ""}
              onChange={(value) => {
                setProficiency(value as Proficiency);
                setTimeout(goToNext, 100);
              }}
              type="radio"
              size="medium"
            >
              <SelectButton.Item
                title={t(m.SegmentationSurvey.QuestionProficiency.Basic)}
                subtitle={t(m.SegmentationSurvey.QuestionProficiency.BasicSubtitle)}
                value="basic"
              />
              <SelectButton.Item
                title={t(m.SegmentationSurvey.QuestionProficiency.Intermediate)}
                subtitle={t(m.SegmentationSurvey.QuestionProficiency.IntermediateSubtitle)}
                value="intermediate"
              />
              <SelectButton.Item
                title={t(m.SegmentationSurvey.QuestionProficiency.Advanced)}
                subtitle={t(m.SegmentationSurvey.QuestionProficiency.AdvancedSubtitle)}
                value="advanced"
              />
            </SelectButton>
          </MultistepForm.Step>
        )}
      </MultistepForm>
    </div>
  );
};

export default SegmentationSurvey;
